import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaArrowRight, FaCheckCircle, FaClock } from 'react-icons/fa';
import './Verify.scss';
import Header from '../Header';
import Footer from '../Footer';
import TelegramPopup from '../../Common/TelegramPopup/TelegramPopup';
import {
  ASTROLESCENT_BRIDGE_URL,
  BASE_DEXTOOLS_URL,
  BASE_DCKS_CONTRACT_ADDRESS,
  BASE_DCKS_CONTRACT_URL,
  BASE_LOCK_END_LABEL,
  BASE_LOCK_URL,
  BASE_PAIR_LABEL,
  BASE_POOL_CONTRACT_URL,
  BASE_PROOF_SUMMARY,
  BASE_UNISWAP_V3_CONTRACT_ADDRESS,
  NO_MINT_PROOF_URL,
  SUPPLY_PROOF_URL,
} from '../../../config/siteContent';

const shortenAddress = (address) =>
  address ? `${address.slice(0, 6)}...${address.slice(-4)}` : '';

const useScrollReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  const observe = useCallback(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold, rootMargin: '0px 0px -40px 0px' }
    );
    if (ref.current) observer.observe(ref.current);
    return observer;
  }, [threshold]);

  useEffect(() => {
    const observer = observe();
    return () => observer?.disconnect();
  }, [observe]);

  return [ref, isVisible];
};

const verificationTracks = [
  {
    title: 'Live now on Base',
    tone: 'green',
    featured: true,
    summary:
      'Base carries the heaviest proof stack, so the links below are grouped as a featured trust panel instead of one long single column.',
    meta: ['Team Finance first', 'Explorer + contracts public', 'Bridge route live'],
    items: [
      {
        label: `Team Finance lock proof (${BASE_PAIR_LABEL})`,
        detail: `${BASE_PROOF_SUMMARY} Current lock display shows an end date of ${BASE_LOCK_END_LABEL}.`,
        href: BASE_LOCK_URL,
        status: 'live',
      },
      {
        label: 'DEXTools pair explorer',
        detail: `Secondary explorer surface for ${BASE_PAIR_LABEL} activity and market discovery.`,
        href: BASE_DEXTOOLS_URL,
        status: 'live',
      },
      {
        label: `DCKS token contract (${shortenAddress(BASE_DCKS_CONTRACT_ADDRESS)})`,
        detail: `Official Base DCKS token contract: ${BASE_DCKS_CONTRACT_ADDRESS}.`,
        href: BASE_DCKS_CONTRACT_URL || null,
        status: BASE_DCKS_CONTRACT_URL ? 'live' : 'pending',
      },
      {
        label: `Base pool contract (${shortenAddress(BASE_UNISWAP_V3_CONTRACT_ADDRESS)})`,
        detail: `Official Base pool contract reference: ${BASE_UNISWAP_V3_CONTRACT_ADDRESS}.`,
        href: BASE_POOL_CONTRACT_URL || null,
        status: BASE_POOL_CONTRACT_URL ? 'live' : 'pending',
      },
      {
        label: 'No mint function proof',
        detail: 'Contract source and read methods on BaseScan.',
        href: NO_MINT_PROOF_URL || null,
        status: NO_MINT_PROOF_URL ? 'live' : 'pending',
      },
      {
        label: 'Transparent supply proof',
        detail: 'Token tracker page for supply visibility references.',
        href: SUPPLY_PROOF_URL || null,
        status: SUPPLY_PROOF_URL ? 'live' : 'pending',
      },
      {
        label: 'Astrolescent bridge route (Radix -> Base)',
        detail: 'Bridge route for cross-chain access from Radix into Base.',
        href: ASTROLESCENT_BRIDGE_URL,
        status: 'live',
      },
    ],
  },
  {
    title: 'Live now on Radix',
    tone: 'cyan',
    items: [
      {
        label: 'Token lock records (JustLock)',
        detail: 'Current lock resource and lock IDs for REDDICKS on Radix.',
        href: 'https://justlock.io/resource/resource_rdx1t42hpqvsk4t42l6aw09hwphd2axvetp6gvas9ztue0p30f4hzdwxrp',
        status: 'live',
      },
      {
        label: 'Official REDDICKS validator',
        detail: 'Validator address and on-chain staking context.',
        href: 'https://dashboard.radixdlt.com/network-staking/validator_rdx1swywqhv3ed5w6dldfs2vamtrsyrdsfd6l9cqen5xqe54597gyckpns',
        status: 'live',
      },
    ],
  },
  {
    title: 'Solana phase next',
    tone: 'violet',
    items: [
      {
        label: 'Future REDDICKS/SOL lock links',
        detail: 'Reserved for public links when the Solana phase is activated.',
        status: 'pending',
      },
    ],
  },
];

const Verify = () => {
  const [tracksRef, tracksVisible] = useScrollReveal(0.08);
  const [guideRef, guideVisible] = useScrollReveal(0.08);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="verify-page" data-scroll-root>
      <Header />

      <main className="verify-content">
        <section className="verify-hero">
          <div className="verify-hero__bg-effects">
            <div className="verify-hero__orb verify-hero__orb--1"></div>
            <div className="verify-hero__orb verify-hero__orb--2"></div>
            <div className="verify-hero__orb verify-hero__orb--3"></div>
          </div>
          <div className="container">
            <div className="verify-hero__content">
              <span className="verify-hero__tagline">Trust Layer</span>
              <h1 className="verify-hero__title">
                <span className="verify-hero__title-line">Verify</span>
                <span className="verify-hero__title-brand">Liquidity</span>
              </h1>
              <p className="verify-hero__subtitle">
                No hype-only claims. Use this page to check what is already
                live, what is already locked on Base, and what is still pending.
                Base is the easiest entry, while Radix remains the active utility core.
              </p>
              <div className="verify-hero__actions">
                <Link to="/buy" className="verify-cta verify-cta--primary">
                  Buy Routes <FaArrowRight />
                </Link>
                <Link to="/tokenomics" className="verify-cta verify-cta--secondary">
                  Tokenomics
                </Link>
              </div>
              <p className="verify-funnel-note">
                Start on Base, verify trust status here, then explore live Radix utility.
              </p>
            </div>
          </div>
        </section>

        <section
          ref={tracksRef}
          className={`verify-tracks ${tracksVisible ? 'visible' : ''}`}
        >
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">Verification Surfaces</h2>
            <div className="verify-track-grid">
              {verificationTracks.map((track) => (
                <article
                  key={track.title}
                  className={`verify-track-card verify-track-card--${track.tone}${track.featured ? ' verify-track-card--featured' : ''}`}
                >
                  {track.summary || track.meta ? (
                    <div className="verify-track-card__intro">
                      <h3>{track.title}</h3>
                      {track.summary ? (
                        <p className="verify-track-card__summary">{track.summary}</p>
                      ) : null}
                      {track.meta?.length ? (
                        <div className="verify-track-card__meta">
                          {track.meta.map((metaItem) => (
                            <span key={metaItem}>{metaItem}</span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <h3>{track.title}</h3>
                  )}
                  <div className={`verify-track-items${track.featured ? ' verify-track-items--featured' : ''}`}>
                    {track.items.map((item, index) => (
                      <div className={`verify-track-item${track.featured ? ' verify-track-item--featured' : ''}`} key={item.label}>
                        <div className="verify-track-item__header">
                          <div className="verify-track-item__title">
                            {track.featured ? (
                              <span className="verify-track-item__index">
                                {String(index + 1).padStart(2, '0')}
                              </span>
                            ) : null}
                            <span className="verify-track-item__label">{item.label}</span>
                          </div>
                          {item.status === 'live' ? (
                            <span className="verify-status verify-status--live">
                              <FaCheckCircle /> Live
                            </span>
                          ) : (
                            <span className="verify-status verify-status--pending">
                              <FaClock /> Pending
                            </span>
                          )}
                        </div>
                        <p>{item.detail}</p>
                        {item.href ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="verify-link-out"
                          >
                            Open Link <FaArrowRight />
                          </a>
                        ) : (
                          <span className="verify-link-pending">
                            Awaiting final publication
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section
          ref={guideRef}
          className={`verify-guide ${guideVisible ? 'visible' : ''}`}
        >
          <div className="section-divider"></div>
          <div className="container">
            <h2 className="section-title centered">How To Verify In 60 Seconds</h2>
            <div className="verify-guide-grid">
              <div className="verify-guide-step">
                <span className="verify-guide-step__number">01</span>
                <p>Check the Radix lock resource and confirm lock IDs are visible.</p>
              </div>
              <div className="verify-guide-step">
                <span className="verify-guide-step__number">02</span>
                <p>Open validator details to confirm address and live staking page.</p>
              </div>
              <div className="verify-guide-step">
                <span className="verify-guide-step__number">03</span>
                <p>For Base, check Team Finance first, then confirm the DEXTools pair surface and official contract addresses.</p>
              </div>
              <div className="verify-guide-step">
                <span className="verify-guide-step__number">04</span>
                <p>Use Buy routes only after reviewing the published lock proof, explorer link, and contract references on this page.</p>
              </div>
            </div>

            <div className="verify-pending-block">
              <h3>Current Base Verification Stack</h3>
              <ul>
                <li>
                  Team Finance lock proof:{' '}
                  <a href={BASE_LOCK_URL} target="_blank" rel="noopener noreferrer">
                    {BASE_PAIR_LABEL}
                  </a>
                </li>
                <li>
                  DEXTools pair explorer:{' '}
                  <a href={BASE_DEXTOOLS_URL} target="_blank" rel="noopener noreferrer">
                    {shortenAddress(BASE_UNISWAP_V3_CONTRACT_ADDRESS)}
                  </a>
                </li>
                <li>
                  Pool contract:{' '}
                  <a href={BASE_POOL_CONTRACT_URL} target="_blank" rel="noopener noreferrer">
                    {BASE_UNISWAP_V3_CONTRACT_ADDRESS}
                  </a>
                </li>
                <li>
                  DCKS contract:{' '}
                  <a href={BASE_DCKS_CONTRACT_URL} target="_blank" rel="noopener noreferrer">
                    {BASE_DCKS_CONTRACT_ADDRESS}
                  </a>
                </li>
                <li>
                  No mint and supply proofs are linked above in the same Base verification panel.
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <TelegramPopup />
    </div>
  );
};

export default Verify;

